import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCategories } from '@/hooks/useCategories';
import { cn } from '@/lib/utils';
import { Search, Menu as MenuIcon, X, Phone, Download, ArrowRight, ChevronRight } from 'lucide-react';
import { productsApi } from '@/services/api';
import type { Product, Category } from '@/types';

// Placeholder for products without images
const PRODUCT_PLACEHOLDER = 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&q=80';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);
  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { useRootCategories } = useCategories();
  const { rootCategories, isLoading } = useRootCategories();
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);

  // Fetch products when category is hovered
  const fetchCategoryProducts = useCallback(async (category: Category) => {
    setLoadingProducts(true);
    try {
      const response = await productsApi.getVisible({
        categorySlug: category.slug,
        limit: 4,
      });
      setCategoryProducts(response.data || []);
    } catch (error) {
      console.warn('Products could not be loaded:', error);
      setCategoryProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  // Handle category hover with debounce
  const handleCategoryHover = useCallback((category: Category | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (category) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredCategory(category);
        fetchCategoryProducts(category);
      }, 150);
    } else {
      // Keep current state when leaving - will reset when dropdown closes
    }
  }, [fetchCategoryProducts]);

  // Reset mega menu state when dropdown closes
  useEffect(() => {
    if (activeDropdown !== 'products') {
      setHoveredCategory(null);
      setCategoryProducts([]);
    }
  }, [activeDropdown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle Resize (Close mobile menu on desktop)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setIsSearchOpen(false);
  }, [location]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Handle escape key for search
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    if (isSearchOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isSearchOpen]);

  // Auto-search after 5 characters with 500ms debounce - show results in modal
  useEffect(() => {
    if (searchQuery.length >= 5 && isSearchOpen) {
      const timer = setTimeout(async () => {
        setIsSearching(true);
        try {
          const response = await productsApi.getVisible({
            search: searchQuery.trim(),
            limit: 6,
          });
          setSearchResults(response.data || []);
        } catch (error) {
          console.warn('Search failed:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, isSearchOpen]);

  // Only show transparent header on homepage when not scrolled
  const isHomePage = location.pathname === '/';
  const shouldBeTransparent = isHomePage && !isScrolled && !isMobileMenuOpen && !activeDropdown;

  // Header Style Classes
  const headerClasses = cn(
    'fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b',
    shouldBeTransparent
      ? 'bg-transparent border-transparent py-6'
      : 'bg-white/95 backdrop-blur-md border-gray-100 py-4 shadow-sm'
  );

  const textClasses = cn(
    'transition-colors duration-300',
    shouldBeTransparent ? 'text-white' : 'text-charcoal'
  );

  return (
    <>
      {/* Top Bar - Contact Info (Only on desktop, visible when scrolled OR not on homepage) */}
      <AnimatePresence>
        {(isScrolled || !isHomePage) && (
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[60] bg-charcoal text-white py-1.5 text-xs hidden lg:block"
          >
            <div className="container mx-auto px-6 lg:px-12 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <a href="tel:+905537942161" className="flex items-center gap-2 text-white hover:text-white/80 transition-colors">
                  <Phone size={12} />
                  <span>0553 794 21 61</span>
                </a>
                <span className="text-white/40">|</span>
                <span className="text-white/70">Pazartesi - Cumartesi: 09:00 - 18:00</span>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="/catalog.pdf"
                  target="_blank"
                  className="flex items-center gap-2 text-white hover:text-white/80 transition-colors"
                >
                  <Download size={12} />
                  <span>Katalog İndir</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className={cn(headerClasses, (isScrolled || !isHomePage) && 'lg:top-7')}>
        <div className="container mx-auto px-6 lg:px-12">
          <nav className="flex items-center justify-between">

            {/* 1. Left: Mobile Menu Trigger (Visible only on mobile) */}
            <div className="lg:hidden">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={textClasses}>
                {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
              </button>
            </div>

            {/* 2. Left: Desktop Logo - Senior Architectural Typography */}
            <Link to="/" className="group relative z-50 grid select-none">
              <span className={cn(
                "font-display text-2xl lg:text-4xl font-bold tracking-tight leading-none",
                textClasses
              )}>
                TURCO
              </span>
              <div className={cn(
                "flex justify-between",
                shouldBeTransparent ? "text-white/90" : "text-charcoal/80"
              )}>
                {'SANDALYE'.split('').map((char, i) => (
                  <span key={i} className="text-[0.6rem] lg:text-[0.8rem] font-semibold leading-none">
                    {char}
                  </span>
                ))}
              </div>
            </Link>

            {/* 3. Center: Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-12">
              {/* Products Dropdown */}
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setActiveDropdown('products')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to="/products"
                  className={cn(
                    "text-sm font-medium tracking-wide uppercase py-4 transition-colors relative hover:opacity-70",
                    textClasses
                  )}
                >
                  Koleksiyonlar
                </Link>

                {/* Mega Menu Dropdown */}
                <AnimatePresence>
                  {activeDropdown === 'products' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 w-[900px] bg-white shadow-2xl border border-gray-100 overflow-hidden mt-0"
                    >
                      <div className="flex min-h-[400px]">
                        {/* Categories List - Left Side */}
                        <div className="w-[280px] p-6 bg-gray-50 border-r border-gray-100 flex flex-col">
                          <h3 className="font-display text-sm font-semibold uppercase tracking-wider mb-4 text-gray-400">Kategoriler</h3>
                          {isLoading ? (
                            <div className="space-y-3">
                              {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-5 bg-gray-200 animate-pulse w-3/4 rounded" />)}
                            </div>
                          ) : (
                            <ul className="space-y-1 flex-1">
                              {rootCategories?.map(cat => (
                                <li key={cat.id}>
                                  <Link
                                    to={`/products?categorySlug=${cat.slug}`}
                                    className={cn(
                                      "flex items-center justify-between px-3 py-2.5 text-sm rounded-md transition-all group",
                                      hoveredCategory?.id === cat.id
                                        ? "bg-charcoal text-white font-medium"
                                        : "text-gray-600 hover:bg-gray-100 hover:text-charcoal"
                                    )}
                                    onMouseEnter={() => handleCategoryHover(cat)}
                                  >
                                    <span>{cat.name}</span>
                                    <ChevronRight className={cn(
                                      "w-4 h-4 transition-all",
                                      hoveredCategory?.id === cat.id
                                        ? "opacity-100 translate-x-0"
                                        : "opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0"
                                    )} />
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* Bottom link */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <Link
                              to="/products"
                              className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-charcoal transition-colors"
                            >
                              Tüm Koleksiyonu Görüntüle
                              <ArrowRight className="w-3 h-3" />
                            </Link>
                          </div>
                        </div>

                        {/* Right Side Content */}
                        <div className="flex-1 p-6">
                          {hoveredCategory ? (
                            <>
                              {/* Header */}
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg text-charcoal">
                                  {hoveredCategory.name}
                                </h3>
                                <Link
                                  to={`/products?categorySlug=${hoveredCategory.slug}`}
                                  className="text-xs font-medium text-gray-500 hover:text-charcoal transition-colors flex items-center gap-1"
                                >
                                  Tümünü Gör
                                  <ArrowRight className="w-3 h-3" />
                                </Link>
                              </div>

                              {/* Products Grid */}
                              <div className="grid grid-cols-4 gap-4">
                                {loadingProducts ? (
                                  [...Array(4)].map((_, i) => (
                                    <div key={i} className="space-y-2">
                                      <div className="aspect-square bg-gray-100 animate-pulse rounded-lg" />
                                      <div className="h-4 bg-gray-100 animate-pulse rounded w-3/4" />
                                    </div>
                                  ))
                                ) : categoryProducts.length > 0 ? (
                                  categoryProducts.slice(0, 4).map((product) => (
                                    <Link
                                      key={product.id}
                                      to={`/products/${product.slug}`}
                                      className="group block space-y-2"
                                    >
                                      <div className="aspect-square bg-white overflow-hidden rounded-lg relative border border-gray-200">
                                        <img
                                          src={product.images?.[0]?.url || PRODUCT_PLACEHOLDER}
                                          alt={product.title}
                                          className="absolute inset-0 object-contain w-full h-full p-2 transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                      </div>
                                      <span className="block text-sm font-medium text-charcoal group-hover:text-gray-600 transition-colors line-clamp-1 mt-2">
                                        {product.title}
                                      </span>
                                    </Link>
                                  ))
                                ) : null}
                              </div>

                              {hoveredCategory.description && (
                                <p className="mt-4 text-xs text-gray-500 line-clamp-2">
                                  {hoveredCategory.description}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              {/* Default: Category cards grid */}
                              <h3 className="font-display text-lg text-charcoal mb-4">Koleksiyonlar</h3>
                              <div className="grid grid-cols-3 gap-4">
                                {rootCategories?.map(cat => (
                                  <Link
                                    key={cat.id}
                                    to={`/products?categorySlug=${cat.slug}`}
                                    className="group block"
                                    onMouseEnter={() => handleCategoryHover(cat)}
                                  >
                                    <div className="aspect-[4/3] bg-white overflow-hidden rounded-lg relative border border-gray-200">
                                      {cat.image?.url ? (
                                        <img
                                          src={cat.image.url}
                                          alt={cat.name}
                                          className="absolute inset-0 object-contain w-full h-full p-2 transition-transform duration-500 group-hover:scale-110"
                                        />
                                      ) : (
                                        <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
                                          <span className="text-gray-300 text-xs">Görsel yok</span>
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                    </div>
                                    <span className="block text-sm font-medium text-charcoal group-hover:text-gray-600 transition-colors mt-2">
                                      {cat.name}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/about" className={cn("text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity", textClasses)}>
                Kurumsal
              </Link>

              <Link to="/projects" className={cn("text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity", textClasses)}>
                Referanslar
              </Link>

              <Link to="/contact" className={cn("text-sm font-medium tracking-wide uppercase hover:opacity-70 transition-opacity", textClasses)}>
                İletişim
              </Link>
            </div>

            {/* 4. Right: Utility Icons */}
            <div className="flex items-center space-x-4 lg:space-x-6">
              {/* Phone - Mobile only, quick call */}
              <a
                href="tel:+905537942161"
                className={cn("lg:hidden hover:opacity-70 transition-opacity", textClasses)}
                aria-label="Ara"
              >
                <Phone size={20} strokeWidth={1.5} />
              </a>

              {/* Search */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className={cn("hover:opacity-70 transition-opacity", textClasses)}
                aria-label="Ara"
              >
                <Search size={20} strokeWidth={1.5} />
              </button>

              {/* CTA Button */}
              <Link
                to="/contact"
                className={cn(
                  "hidden lg:inline-flex items-center gap-2 px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300",
                  isScrolled || activeDropdown
                    ? "bg-charcoal text-white hover:bg-gray-800 hover:text-white"
                    : "bg-white text-charcoal hover:bg-gray-100"
                )}
              >
                Teklif İste
              </Link>
            </div>

          </nav>
        </div>

      </header>

      {/* Mobile Menu - RH.com Inspired Left Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsCollectionsOpen(false);
              }}
              className="fixed inset-0 z-[9998] bg-black/50 lg:hidden"
            />

            {/* Left Drawer Panel - RH Style */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 left-0 bottom-0 z-[9999] w-[85%] max-w-[360px] bg-white shadow-2xl lg:hidden flex flex-col"
            >
              {/* Header with Logo & Close */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="grid"
                >
                  <span className="font-display text-xl font-bold tracking-tight text-charcoal leading-none">
                    TURCO
                  </span>
                  <div className="flex justify-between text-charcoal/80">
                    {'SANDALYE'.split('').map((char, i) => (
                      <span key={i} className="text-[0.5rem] font-semibold leading-none">
                        {char}
                      </span>
                    ))}
                  </div>
                </Link>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsCollectionsOpen(false);
                  }}
                  className="w-10 h-10 flex items-center justify-center text-charcoal hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Menu Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <AnimatePresence mode="wait">
                  {!isCollectionsOpen ? (
                    /* Main Menu */
                    <motion.div
                      key="main-menu"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="py-6"
                    >
                      {/* Koleksiyonlar - Opens Sub-Menu */}
                      <button
                        onClick={() => setIsCollectionsOpen(true)}
                        className="w-full flex items-center justify-between px-6 py-4 text-left group"
                      >
                        <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                          Koleksiyonlar
                        </span>
                        <ChevronRight size={18} className="text-gray-400 group-hover:text-charcoal transition-colors" />
                      </button>

                      {/* Kurumsal */}
                      <Link
                        to="/about"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full flex items-center justify-between px-6 py-4 group"
                      >
                        <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                          Kurumsal
                        </span>
                        <ChevronRight size={18} className="text-gray-400 group-hover:text-charcoal transition-colors" />
                      </Link>

                      {/* Referanslar */}
                      <Link
                        to="/projects"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full flex items-center justify-between px-6 py-4 group"
                      >
                        <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                          Referanslar
                        </span>
                        <ChevronRight size={18} className="text-gray-400 group-hover:text-charcoal transition-colors" />
                      </Link>

                      {/* İletişim */}
                      <Link
                        to="/contact"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full flex items-center justify-between px-6 py-4 group"
                      >
                        <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                          İletişim
                        </span>
                        <ChevronRight size={18} className="text-gray-400 group-hover:text-charcoal transition-colors" />
                      </Link>

                      {/* Divider */}
                      <div className="my-4 mx-6 border-t border-gray-100" />

                      {/* Quick Actions */}
                      <a
                        href="tel:+905537942161"
                        className="flex items-center gap-3 px-6 py-3 text-sm text-gray-600 hover:text-charcoal transition-colors"
                      >
                        <Phone size={16} />
                        <span>0553 794 21 61</span>
                      </a>

                      <a
                        href="/catalog.pdf"
                        target="_blank"
                        className="flex items-center gap-3 px-6 py-3 text-sm text-gray-600 hover:text-charcoal transition-colors"
                      >
                        <Download size={16} />
                        <span>Katalog İndir</span>
                      </a>
                    </motion.div>
                  ) : (
                    /* Collections Sub-Menu - RH Style */
                    <motion.div
                      key="collections-menu"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      className="py-2"
                    >
                      {/* Back Button & Title */}
                      <button
                        onClick={() => setIsCollectionsOpen(false)}
                        className="w-full flex items-center gap-3 px-6 py-4 border-b border-gray-100 group"
                      >
                        <ChevronRight size={18} className="rotate-180 text-gray-400 group-hover:text-charcoal transition-colors" />
                        <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                          Koleksiyonlar
                        </span>
                      </button>

                      {/* Category List */}
                      <div className="py-4">
                        {/* Dynamic Categories */}
                        {rootCategories?.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/products?categorySlug=${cat.slug}`}
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              setIsCollectionsOpen(false);
                            }}
                            className="w-full flex items-center justify-between px-6 py-4 group"
                          >
                            <span className="text-sm font-medium uppercase tracking-[0.15em] text-charcoal">
                              {cat.name}
                            </span>
                            <ChevronRight size={18} className="text-gray-400 group-hover:text-charcoal transition-colors" />
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Fixed Bottom CTA */}
              <div className="p-4 border-t border-gray-100">
                <Link
                  to="/contact"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsCollectionsOpen(false);
                  }}
                  className="flex items-center justify-center gap-2 w-full py-4 bg-charcoal text-white text-sm font-medium uppercase tracking-[0.15em] hover:bg-black transition-colors"
                >
                  Teklif İste
                  <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-start justify-center pt-32"
            onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery('');
              setSearchResults([]);
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: -30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-2xl px-6"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSearchSubmit}>
                <div className="relative">
                  <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ürün, kategori veya koleksiyon ara..."
                    className="w-full py-6 pl-16 pr-6 text-xl bg-white border-0 
                               focus:outline-none focus:ring-4 focus:ring-gold/30
                               placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-charcoal transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </form>

              <p className="text-center text-white/50 text-sm mt-4">
                5 karakter sonra otomatik arama yapılır veya kapatmak için ESC tuşunu kullanın
              </p>

              {/* Search Results */}
              {isSearching && (
                <div className="mt-6 text-center text-white/70">
                  <div className="inline-block w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <p className="mt-2 text-sm">Aranıyor...</p>
                </div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <div className="mt-6">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Arama Sonuçları</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {searchResults.map((product) => (
                      <Link
                        key={product.id}
                        to={`/products/${product.slug}`}
                        onClick={() => {
                          setIsSearchOpen(false);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="group block"
                      >
                        <div className="aspect-square bg-white/10 overflow-hidden rounded-lg relative">
                          <img
                            src={product.images?.[0]?.url || PRODUCT_PLACEHOLDER}
                            alt={product.title}
                            className="absolute inset-0 object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <p className="mt-2 text-white text-sm font-medium truncate group-hover:text-white/80 transition-colors">
                          {product.title}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {!isSearching && searchQuery.length >= 5 && searchResults.length === 0 && (
                <div className="mt-6 text-center text-white/50">
                  <p>Sonuç bulunamadı</p>
                </div>
              )}

              {/* Quick Links - Product Types (show when no search results) */}
              {searchResults.length === 0 && !isSearching && (
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {[
                    { label: 'Sandalye', type: 'chair' },
                    { label: 'Masa', type: 'table' },
                    { label: 'Takım', type: 'set' },
                  ].map((item) => (
                    <button
                      key={item.type}
                      onClick={() => {
                        setIsSearchOpen(false);
                        setSearchQuery('');
                        setSearchResults([]);
                        // Force navigation even if same route by using window.location
                        const targetUrl = `/products?productType=${item.type}`;
                        if (location.pathname + location.search === targetUrl) {
                          // Same URL - force reload
                          window.location.href = targetUrl;
                        } else {
                          navigate(targetUrl);
                        }
                      }}
                      className="bg-white/10 hover:bg-white/20 text-white text-center py-3 px-4
                                 transition-colors text-sm"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
